import {
  type Artifact,
  isSupersedableType,
  ARTIFACT_RESPONDED_STATUS,
} from "../types/artifact";

// The canonical `artifacts` channel reducer — used identically on the backend
// graph channel and the client stream reducer. Whole-artifact replace by id
// (NOT a field-merge, NOT a row-level merge): the caller always submits the
// complete artifact, so there is one unambiguous rule and zero client/server
// drift. The Artifact shape lives in shared/types/artifact.ts.
//
// Supersedable types (see isSupersedableType) hold at most one active artifact:
// introducing a NEW one of that type finalizes its active siblings, so a re-run
// can't leave multiple live tables. Editing an existing id (e.g. the approve
// submit) is a whole-replace and supersedes nothing.

export function artifactsMerge(
  incoming: Artifact[],
  current: Artifact[] | undefined
): Artifact[] {
  if (!current || current.length === 0) return incoming;
  if (incoming.length === 0) return []; // explicit clear

  let merged = [...current];
  const indexById = new Map(merged.map((a, i) => [a.id, i]));

  for (const artifact of incoming) {
    const existingIdx = indexById.get(artifact.id);
    if (existingIdx !== undefined) {
      merged[existingIdx] = artifact; // whole replace (edit) — no supersession
      continue;
    }
    // A genuinely new artifact. If its type is supersedable, finalize the active
    // siblings of that type (mark them responded → read-only) before appending.
    if (isSupersedableType(artifact.type)) {
      merged = merged.map((a) =>
        a.type === artifact.type && a.status !== ARTIFACT_RESPONDED_STATUS
          ? { ...a, status: ARTIFACT_RESPONDED_STATUS }
          : a
      );
    }
    indexById.set(artifact.id, merged.length);
    merged.push(artifact);
  }
  return merged;
}
